/**
 * BuildingDepartmentJourney — v2 with city context + terrace finale
 * ------------------------------------------------------------------
 * One Successlane HQ building sits at the centre of a small city block. The
 * character enters at the ground floor and zig-zags up through 5 floors via
 * the elevator on the right. After Floor 5, they ride the elevator up to the
 * ROOFTOP TERRACE where they jump with their arms raised in victory and
 * confetti erupts. Loop repeats.
 *
 * Around the building: trees, café, charging-station + parked motorbikes near
 * the road, pedestrians standing/looking, a car driving past.
 *
 * Character is ALWAYS visible — never fades. The story is told purely through
 * continuous motion. Click any floor for the standard flipbook info-card.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

const C = {
  // Sky / ground
  skyTop: '#FEFCE8', skyBot: '#E8F1FC',
  groundTop: '#FCF4D3', groundBot: '#E2DFB4',
  road: '#E0E2E6', roadEdge: '#C4C6CB',
  sidewalk: '#F4EDD8',
  // Building
  buildWall: '#FFFFFF', buildSide: '#F2F4F7',
  buildAccent: '#7DD3C0', buildAccentDark: '#5BAFA2',
  buildOutline: '#0F2E4D', floorLine: '#CFE8DC',
  // Brand
  navy: '#0D1F3C', primary: '#1A6FD6', primaryLight: '#E8F1FC',
  gold: '#D4A742', green: '#10B981', greenSoft: '#86EFAC',
  // Character
  red: '#E04E3A', charBlue: '#2D54A5',
  charSkin: '#F3D0AC', charHair: '#3D2A20', charShoe: '#3A2010',
  // Misc
  treeMid: '#74C797', treeDark: '#3D8A60', treeTrunk: '#7B5237',
  lampPole: '#33485E',
  textMid: '#475569', textFaint: '#94A3B8',
  border: '#E5E9F0', white: '#FFFFFF',
  // Pedestrians
  ped1: '#7E4FB8', ped2: '#3B82F6', ped3: '#FB923C', ped4: '#0EA5E9', ped5: '#EC4899',
};

// ─── Layout ─────────────────────────────────────────────────────────────────
const VB_W = 1400;
const VB_H = 820;

const FLOOR_H = 75;
const FLOORS = 5;
const BLD_W = 550;
const BLD_X = 425;
const GROUND_Y = 575;                              // building meets sidewalk
const BLD_Y_TOP = GROUND_Y - FLOORS * FLOOR_H;     // top of Floor 5 = 200
const TERRACE_Y_TOP = BLD_Y_TOP - 60;              // top of terrace = 140
const ELEVATOR_X = BLD_X + BLD_W - 60;
const ELEVATOR_W = 48;

const floorY = (idx) => GROUND_Y - idx * FLOOR_H - 4;
const TERRACE_FLOOR_Y = TERRACE_Y_TOP + 50;

const ROAD_Y = 720;

// ─── Stations ───────────────────────────────────────────────────────────────
const STATIONS = [
  { id: 'eval',     step: 1, title: 'Free Profile Evaluation',
    blurb: 'No credit card. Drop your LinkedIn URL or PDF and get a recruiter-voice score in 60 seconds — exactly what hiring managers see in the 8-second test.',
    ctaPrimary: { label: 'Try it free', to: '/analyze' } },
  { id: 'checkout', step: 2, title: 'Secure Checkout',
    blurb: 'One-time ₹999 payment via Razorpay. No subscription, no upsells. Order ID gets generated and the rewrite work begins instantly.',
    ctaPrimary: { label: 'Try it free', to: '/analyze' } },
  { id: 'expert',   step: 3, title: 'Expert Rewrite',
    blurb: 'Our expert + AI rewrite your Headline, About, and Skills using recruiter-tested patterns and keywords matched to your dream companies.',
    ctaPrimary: { label: 'Try it free', to: '/analyze' } },
  { id: 'recruiter',step: 4, title: 'Recruiter Certification',
    blurb: 'A domain-matched, LinkedIn-verified recruiter reviews your rewrite, makes any needed edits, and signs off — their name appears on your certification.',
    ctaPrimary: { label: 'Try it free', to: '/analyze' } },
  { id: 'delivery', step: 5, title: 'Delivered to Your Inbox',
    blurb: 'Within 48 hours, the final certified profile arrives in your inbox as an HTML attachment with copy buttons — paste straight into LinkedIn.',
    ctaPrimary: { label: 'Try it free', to: '/analyze' } },
];

// ═══ CHARACTER ══════════════════════════════════════════════════════════════
// mode: 'walking' | 'idle' | 'victory'
const Character = ({ flipX = false, mode = 'walking' }) => {
  const walking = mode === 'walking';
  const victory = mode === 'victory';
  return (
    <motion.g
      animate={
        victory ? { y: [0, -14, 0] } :
        walking ? { y: [0, -1, 0, -1, 0] } :
        { y: 0 }
      }
      transition={
        victory ? { duration: 0.7, repeat: Infinity, ease: 'easeInOut' } :
        walking ? { duration: 0.45, repeat: Infinity, ease: 'easeInOut' } :
        { duration: 0 }
      }
      style={{ transform: flipX ? 'scaleX(-1)' : 'none' }}
    >
      <ellipse cx={0} cy={9} rx={9} ry={2.4} fill={C.navy} opacity={0.18} />
      {/* Legs */}
      <motion.g
        animate={walking ? { rotate: [-22, 22, -22] } : { rotate: 0 }}
        transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
        style={{ originX: '0px', originY: '-2px' }}
      >
        <rect x={-4} y={-2} width={3.4} height={10} rx={1.3} fill={C.charBlue} stroke={C.buildOutline} strokeWidth={0.6} />
        <rect x={-4} y={6}  width={4.5} height={3} rx={1.2} fill={C.charShoe} />
      </motion.g>
      <motion.g
        animate={walking ? { rotate: [22, -22, 22] } : { rotate: 0 }}
        transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
        style={{ originX: '0px', originY: '-2px' }}
      >
        <rect x={0.5} y={-2} width={3.4} height={10} rx={1.3} fill={C.charBlue} stroke={C.buildOutline} strokeWidth={0.6} />
        <rect x={-0.5} y={6} width={4.5} height={3} rx={1.2} fill={C.charShoe} />
      </motion.g>
      {/* Torso */}
      <rect x={-5.5} y={-15} width={11} height={14} rx={2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.7} />
      <rect x={-3.4} y={-15} width={1.4} height={9} fill={C.charBlue} />
      <rect x={2.0}  y={-15} width={1.4} height={9} fill={C.charBlue} />
      {/* Arms */}
      {victory ? (
        // Arms straight up in victory
        <>
          <g style={{ originX: '-5px', originY: '-14px' }} transform="rotate(150)">
            <rect x={-7.5} y={-14} width={3} height={10} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
            <circle cx={-6} cy={-4} r={1.8} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
          </g>
          <g style={{ originX: '5px', originY: '-14px' }} transform="rotate(-150)">
            <rect x={4.5} y={-14} width={3} height={10} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
            <circle cx={6} cy={-4} r={1.8} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
          </g>
        </>
      ) : (
        <>
          <motion.g
            animate={walking ? { rotate: [22, -22, 22] } : { rotate: 0 }}
            transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
            style={{ originX: '-5px', originY: '-14px' }}
          >
            <rect x={-7.5} y={-14} width={3} height={9} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
            <circle cx={-6} cy={-5} r={1.6} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
          </motion.g>
          <motion.g
            animate={walking ? { rotate: [-22, 22, -22] } : { rotate: 0 }}
            transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
            style={{ originX: '5px', originY: '-14px' }}
          >
            <rect x={4.5} y={-14} width={3} height={9} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
            <circle cx={6} cy={-5} r={1.6} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
          </motion.g>
        </>
      )}
      {/* Head */}
      <circle cx={0} cy={-21} r={5.5} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.6} />
      <path d={`M -5 -25 Q 0 -29 5 -25 L 4 -22 L -4 -22 Z`} fill={C.charHair} stroke={C.buildOutline} strokeWidth={0.5} />
      <circle cx={-1.7} cy={-21} r={0.7} fill={C.buildOutline} />
      <circle cx={1.7}  cy={-21} r={0.7} fill={C.buildOutline} />
      {/* Smile in victory mode */}
      {victory && (
        <path d="M -2 -18.5 Q 0 -17 2 -18.5" stroke={C.buildOutline} strokeWidth={0.8} fill="none" strokeLinecap="round" />
      )}
      {/* Cap */}
      <path d={`M -5 -25 Q 0 -29 5 -25 L 5 -23 L -5 -23 Z`} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
      <circle cx={0} cy={-25.5} r={1.4} fill={C.white} stroke={C.buildOutline} strokeWidth={0.5} />
    </motion.g>
  );
};

// ═══ PEDESTRIAN (small simple human, standing) ══════════════════════════════
const Pedestrian = ({ x, y, color = C.ped1, idle = true, scale = 1 }) => (
  <motion.g
    transform={`translate(${x}, ${y}) scale(${scale})`}
    animate={idle ? { y: [0, -0.6, 0] } : {}}
    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
  >
    <ellipse cx={0} cy={6} rx={6.5} ry={1.6} fill={C.navy} opacity={0.18} />
    {/* Legs */}
    <rect x={-3} y={-2} width={2.4} height={7} rx={1} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.5} />
    <rect x={0.6} y={-2} width={2.4} height={7} rx={1} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.5} />
    {/* Body */}
    <rect x={-4} y={-12} width={8} height={11} rx={2} fill={color} stroke={C.buildOutline} strokeWidth={0.6} />
    {/* Arms (close to body) */}
    <rect x={-5} y={-11} width={2} height={8} rx={1} fill={color} stroke={C.buildOutline} strokeWidth={0.5} />
    <rect x={3} y={-11} width={2} height={8} rx={1} fill={color} stroke={C.buildOutline} strokeWidth={0.5} />
    {/* Head */}
    <circle cx={0} cy={-16} r={4} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
    {/* Hair */}
    <path d={`M -4 -19 Q 0 -22 4 -19 L 4 -17 L -4 -17 Z`} fill={C.charHair} />
  </motion.g>
);

// ═══ ENVIRONMENT PIECES ═════════════════════════════════════════════════════
const PalmTree = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x}, ${y}) scale(${scale})`}>
    <path d="M -2 18 Q -3 8 -1 -8 L 1 -8 Q 3 8 2 18 Z" fill={C.treeTrunk} stroke={C.buildOutline} strokeWidth={0.8} />
    {[-90, -55, -15, 25, 65, 105].map((rot, i) => (
      <g key={i} transform={`rotate(${rot})`}>
        <path d="M 0 -10 Q 12 -16 24 -12 Q 14 -10 0 -10 Z" fill={C.treeMid} stroke={C.buildOutline} strokeWidth={0.7} />
      </g>
    ))}
    <circle cx={0} cy={-10} r={2.5} fill={C.treeDark} />
    <circle cx={2} cy={-7} r={1.5} fill={C.charShoe} stroke={C.buildOutline} strokeWidth={0.5} />
  </g>
);

const RoundTree = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x}, ${y}) scale(${scale})`}>
    <rect x={-2.5} y={0} width={5} height={14} fill={C.treeTrunk} stroke={C.buildOutline} strokeWidth={0.7} />
    <circle cx={0} cy={-6} r={16} fill={C.treeMid} stroke={C.buildOutline} strokeWidth={1} />
    <circle cx={-7} cy={-3} r={8} fill={C.treeDark} opacity={0.55} />
    <circle cx={6} cy={-10} r={7} fill={C.treeDark} opacity={0.45} />
  </g>
);

const Motorbike = ({ x, y, color = C.red }) => (
  <g transform={`translate(${x}, ${y})`}>
    <ellipse cx={0} cy={5} rx={16} ry={2} fill={C.navy} opacity={0.18} />
    <circle cx={-9} cy={0} r={4} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={-9} cy={0} r={2} fill={C.buildSide} />
    <circle cx={9}  cy={0} r={4} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={9}  cy={0} r={2} fill={C.buildSide} />
    <path d="M -9 -2 L 5 -8 L 12 -4 L 9 -2 Z" fill={color} stroke={C.buildOutline} strokeWidth={0.7} />
    <rect x={-4} y={-9} width={9} height={2.5} rx={1} fill={C.navy} />
    <rect x={4} y={-15} width={1.2} height={9} fill={C.navy} />
    <rect x={2} y={-16} width={6} height={2} rx={1} fill={C.navy} />
  </g>
);

const ChargingStation = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={-8} y={-30} width={16} height={30} rx={3} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={1} />
    <rect x={-5} y={-26} width={10} height={8} rx={1.5} fill={C.navy} />
    <motion.rect
      x={-3.5} y={-24.5} width={3} height={5} fill={C.green}
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
    />
    <path d="M -1.5 -16 L -3 -10 L 0 -10 L -1.5 -4 L 2 -12 L -1 -12 Z" fill={C.gold} stroke={C.buildOutline} strokeWidth={0.5} />
    <path d="M 6 -14 Q 12 -8 14 -2 Q 14 2 12 4" fill="none" stroke={C.navy} strokeWidth={1.4} strokeLinecap="round" />
    <rect x={11} y={3} width={4} height={3} rx={1} fill={C.navy} />
  </g>
);

const Car = ({ x, y, color = C.primary }) => (
  <g transform={`translate(${x}, ${y})`}>
    <ellipse cx={0} cy={9} rx={22} ry={3} fill={C.navy} opacity={0.18} />
    <rect x={-22} y={-2} width={44} height={9} rx={3} fill={color} stroke={C.buildOutline} strokeWidth={0.8} />
    <path d="M -16 -2 L -10 -10 L 10 -10 L 16 -2 Z" fill={color} stroke={C.buildOutline} strokeWidth={0.8} />
    <path d="M -14 -3 L -10 -9 L -1 -9 L -1 -3 Z" fill={C.primaryLight} stroke={C.buildOutline} strokeWidth={0.5} />
    <path d="M 1 -3 L 1 -9 L 10 -9 L 14 -3 Z" fill={C.primaryLight} stroke={C.buildOutline} strokeWidth={0.5} />
    <circle cx={-13} cy={7} r={3} fill={C.navy} />
    <circle cx={-13} cy={7} r={1.4} fill={C.buildSide} />
    <circle cx={13} cy={7}  r={3} fill={C.navy} />
    <circle cx={13} cy={7}  r={1.4} fill={C.buildSide} />
    <circle cx={20} cy={2} r={1.2} fill={C.gold} />
  </g>
);

const Shop = ({ x, y, name = 'CAFÉ', accent = C.red }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={-40} y={-50} width={80} height={50} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.2} />
    <path d="M -45 -50 L 0 -70 L 45 -50 Z" fill={accent} stroke={C.buildOutline} strokeWidth={1.2} />
    {/* Window */}
    <rect x={-30} y={-42} width={32} height={26} fill={C.primaryLight} stroke={C.buildOutline} strokeWidth={0.9} />
    <line x1={-30} y1={-29} x2={2} y2={-29} stroke={C.buildOutline} strokeWidth={0.6} />
    <line x1={-14} y1={-42} x2={-14} y2={-16} stroke={C.buildOutline} strokeWidth={0.6} />
    {/* Door */}
    <rect x={10} y={-32} width={20} height={32} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={0.9} />
    <circle cx={26} cy={-17} r={1.2} fill={C.gold} />
    {/* Awning stripes */}
    <rect x={-40} y={-15} width={16} height={4} fill={accent} />
    <rect x={-24} y={-15} width={16} height={4} fill={C.white} stroke={C.buildOutline} strokeWidth={0.4} />
    <rect x={-8}  y={-15} width={16} height={4} fill={accent} />
    {/* Sign */}
    <rect x={-22} y={-68} width={44} height={10} rx={2} fill={C.navy} />
    <text x={0} y={-60.5} textAnchor="middle" fontSize={7} fontWeight={700} fill={C.white} fontFamily="'Plus Jakarta Sans', sans-serif">{name}</text>
  </g>
);

const StreetLamp = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={-1} y={-32} width={2} height={32} fill={C.lampPole} stroke={C.buildOutline} strokeWidth={0.6} />
    <ellipse cx={0} cy={-36} rx={4} ry={3.5} fill="#FFF3C8" stroke={C.buildOutline} strokeWidth={0.7} />
  </g>
);

// ═══ FLOOR CONTENTS (compressed for 75px floor height) ══════════════════════
const Floor1 = () => (
  <g>
    <rect x={BLD_X + 60}  y={GROUND_Y - 38} width={70} height={20} rx={3} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={0.9} />
    <text x={BLD_X + 95} y={GROUND_Y - 24} textAnchor="middle" fontSize={6} fontWeight={700} fill={C.navy}>RECEPTION</text>
    <rect x={BLD_X + 170} y={GROUND_Y - 56} width={42} height={42} rx={3} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.9} />
    <rect x={BLD_X + 174} y={GROUND_Y - 52} width={34} height={26} rx={2} fill={C.primaryLight} />
    <text x={BLD_X + 191} y={GROUND_Y - 38} textAnchor="middle" fontSize={7} fontWeight={800} fill={C.primary}>in</text>
    <text x={BLD_X + 191} y={GROUND_Y - 28} textAnchor="middle" fontSize={4.5} fontWeight={600} fill={C.primary}>IMPORT</text>
    {/* Plant */}
    <rect x={BLD_X + 250} y={GROUND_Y - 26} width={10} height={12} fill={C.charShoe} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={BLD_X + 255} cy={GROUND_Y - 32} r={9} fill={C.buildAccentDark} stroke={C.buildOutline} strokeWidth={0.6} />
    {/* Floor label */}
    <rect x={BLD_X + 310} y={GROUND_Y - 52} width={150} height={18} rx={3} fill={C.primaryLight} stroke={C.primary} strokeWidth={1} />
    <text x={BLD_X + 385} y={GROUND_Y - 40} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.primary}>FLOOR 1 · FREE EVALUATION</text>
  </g>
);

const Floor2 = () => (
  <g>
    <rect x={BLD_X + 80} y={floorY(1) - 32} width={120} height={24} rx={3} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={0.9} />
    <rect x={BLD_X + 122} y={floorY(1) - 56} width={28} height={22} rx={2} fill={C.navy} />
    <rect x={BLD_X + 125} y={floorY(1) - 53} width={22} height={11} rx={1.5} fill={C.green} />
    <text x={BLD_X + 136} y={floorY(1) - 44} textAnchor="middle" fontSize={6.5} fontWeight={800} fill={C.white}>₹999</text>
    {/* PAID stamp */}
    <motion.g
      animate={{ scale: [0.85, 1.15, 1], opacity: [0.6, 1, 1] }}
      transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 1, ease: 'easeOut' }}
    >
      <circle cx={BLD_X + 220} cy={floorY(1) - 36} r={10} fill="none" stroke={C.green} strokeWidth={1.6} />
      <text x={BLD_X + 220} y={floorY(1) - 33} textAnchor="middle" fontSize={7} fontWeight={800} fill={C.green}>✓</text>
    </motion.g>
    <rect x={BLD_X + 250} y={floorY(1) - 46} width={64} height={14} rx={7} fill={C.primaryLight} stroke={C.primary} strokeWidth={0.9} />
    <text x={BLD_X + 282} y={floorY(1) - 36} textAnchor="middle" fontSize={6.5} fontWeight={700} fill={C.primary} fontFamily="monospace">#KA000042</text>
    <rect x={BLD_X + 330} y={floorY(1) - 52} width={130} height={18} rx={3} fill={C.primaryLight} stroke={C.primary} strokeWidth={1} />
    <text x={BLD_X + 395} y={floorY(1) - 40} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.primary}>FLOOR 2 · CHECKOUT</text>
  </g>
);

const Floor3 = () => (
  <g>
    <rect x={BLD_X + 80} y={floorY(2) - 32} width={42} height={22} rx={2} fill={C.charShoe} stroke={C.buildOutline} strokeWidth={0.9} />
    <rect x={BLD_X + 84} y={floorY(2) - 54} width={34} height={22} rx={2} fill={C.navy} />
    <rect x={BLD_X + 87} y={floorY(2) - 51} width={8} height={1.5} fill={C.green} />
    <rect x={BLD_X + 87} y={floorY(2) - 48} width={18} height={1.5} fill={C.green} />
    <rect x={BLD_X + 87} y={floorY(2) - 45} width={14} height={1.5} fill={C.gold} />
    <rect x={BLD_X + 87} y={floorY(2) - 42} width={22} height={1.5} fill={C.green} />
    <rect x={BLD_X + 87} y={floorY(2) - 39} width={16} height={1.5} fill={C.primary} />
    <rect x={BLD_X + 135} y={floorY(2) - 50} width={32} height={24} rx={2} fill={C.navy} />
    <motion.rect
      x={BLD_X + 139} y={floorY(2) - 46} width={24} height={16} rx={1.5} fill={C.buildAccent}
      animate={{ fill: [C.buildAccent, C.gold, C.buildAccent] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
    />
    <text x={BLD_X + 151} y={floorY(2) - 34} textAnchor="middle" fontSize={6} fontWeight={700} fill={C.navy}>AI</text>
    <rect x={BLD_X + 185} y={floorY(2) - 48} width={92} height={14} rx={7} fill={C.greenSoft} stroke={C.green} strokeWidth={0.9} />
    <text x={BLD_X + 231} y={floorY(2) - 38} textAnchor="middle" fontSize={6} fontWeight={700} fill={C.green}>HEADLINE ✓ ABOUT ✓ SKILLS ✓</text>
    <rect x={BLD_X + 330} y={floorY(2) - 52} width={130} height={18} rx={3} fill={C.primaryLight} stroke={C.primary} strokeWidth={1} />
    <text x={BLD_X + 395} y={floorY(2) - 40} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.primary}>FLOOR 3 · EXPERT REWRITE</text>
  </g>
);

const Floor4 = () => (
  <g>
    <rect x={BLD_X + 80} y={floorY(3) - 28} width={80} height={16} rx={2} fill={C.charShoe} stroke={C.buildOutline} strokeWidth={0.9} />
    {/* Recruiter */}
    <g transform={`translate(${BLD_X + 110}, ${floorY(3) - 36})`}>
      <rect x={-5} y={-10} width={10} height={11} rx={2} fill={C.navy} />
      <rect x={-4} y={-10} width={8} height={2} fill={C.gold} />
      <circle cx={0} cy={-15} r={4} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
      <path d="M -3 -18 Q 0 -20 3 -18 L 3 -16 L -3 -16 Z" fill={C.charHair} />
    </g>
    <rect x={BLD_X + 170} y={floorY(3) - 26} width={28} height={18} rx={1.5} fill={C.white} stroke={C.buildOutline} strokeWidth={0.7} />
    <line x1={BLD_X + 173} y1={floorY(3) - 22} x2={BLD_X + 195} y2={floorY(3) - 22} stroke={C.textFaint} strokeWidth={0.5} />
    <line x1={BLD_X + 173} y1={floorY(3) - 19} x2={BLD_X + 195} y2={floorY(3) - 19} stroke={C.textFaint} strokeWidth={0.5} />
    <motion.g
      animate={{ scale: [0, 1.3, 1], rotate: [-20, -10, -5], opacity: [0, 1, 1] }}
      transition={{ duration: 1, repeat: Infinity, repeatDelay: 2, ease: 'backOut' }}
    >
      <circle cx={BLD_X + 187} cy={floorY(3) - 19} r={8} fill="none" stroke={C.green} strokeWidth={1.8} />
      <text x={BLD_X + 187} y={floorY(3) - 16} textAnchor="middle" fontSize={8} fontWeight={800} fill={C.green}>✓</text>
    </motion.g>
    <motion.circle cx={BLD_X + 220} cy={floorY(3) - 48} r={1.4} fill={C.gold}
      animate={{ scale: [0, 1.5, 0], opacity: [0, 1, 0] }}
      transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 0.6 }}
    />
    <motion.circle cx={BLD_X + 250} cy={floorY(3) - 32} r={1.4} fill={C.gold}
      animate={{ scale: [0, 1.5, 0], opacity: [0, 1, 0] }}
      transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1.0, delay: 0.4 }}
    />
    <rect x={BLD_X + 330} y={floorY(3) - 52} width={130} height={18} rx={3} fill={C.primaryLight} stroke={C.primary} strokeWidth={1} />
    <text x={BLD_X + 395} y={floorY(3) - 40} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.primary}>FLOOR 4 · RECRUITER CERT</text>
  </g>
);

const Floor5 = () => (
  <g>
    {/* Mailbox */}
    <rect x={BLD_X + 80} y={floorY(4) - 32} width={26} height={22} rx={3} fill={C.primary} stroke={C.buildOutline} strokeWidth={0.9} />
    <rect x={BLD_X + 85} y={floorY(4) - 28} width={16} height={3} rx={1.5} fill={C.white} />
    {/* Letter flying */}
    <motion.g
      animate={{ y: [0, -8, -16], opacity: [0, 1, 0] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut', times: [0, 0.5, 1] }}
    >
      <rect x={BLD_X + 90} y={floorY(4) - 46} width={16} height={10} rx={1.5} fill={C.white} stroke={C.primary} strokeWidth={1} />
      <path d={`M ${BLD_X + 90} ${floorY(4) - 46} L ${BLD_X + 98} ${floorY(4) - 41} L ${BLD_X + 106} ${floorY(4) - 46}`} fill="none" stroke={C.primary} strokeWidth={1} />
    </motion.g>
    {/* Monitor */}
    <rect x={BLD_X + 130} y={floorY(4) - 52} width={44} height={28} rx={2} fill={C.navy} />
    <rect x={BLD_X + 134} y={floorY(4) - 48} width={36} height={20} rx={1.5} fill={C.greenSoft} />
    <text x={BLD_X + 152} y={floorY(4) - 36} textAnchor="middle" fontSize={7} fontWeight={800} fill={C.green}>SENT ✓</text>
    {/* Globe */}
    <circle cx={BLD_X + 200} cy={floorY(4) - 36} r={10} fill={C.primaryLight} stroke={C.primary} strokeWidth={1.1} />
    <ellipse cx={BLD_X + 200} cy={floorY(4) - 36} rx={10} ry={4} fill="none" stroke={C.primary} strokeWidth={0.7} />
    <line x1={BLD_X + 190} y1={floorY(4) - 36} x2={BLD_X + 210} y2={floorY(4) - 36} stroke={C.primary} strokeWidth={0.7} />
    <rect x={BLD_X + 222} y={floorY(4) - 46} width={80} height={14} rx={7} fill={C.greenSoft} stroke={C.green} strokeWidth={0.9} />
    <text x={BLD_X + 262} y={floorY(4) - 36} textAnchor="middle" fontSize={6} fontWeight={700} fill={C.green}>DELIVERED TO INBOX</text>
    <rect x={BLD_X + 330} y={floorY(4) - 52} width={130} height={18} rx={3} fill={C.primaryLight} stroke={C.primary} strokeWidth={1} />
    <text x={BLD_X + 395} y={floorY(4) - 40} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.primary}>FLOOR 5 · DELIVERY</text>
  </g>
);

// ═══ TERRACE (rooftop, with character finale) ══════════════════════════════
const Terrace = ({ celebrating }) => (
  <g>
    {/* Terrace floor (parapet/railing top) */}
    <rect x={BLD_X - 4} y={TERRACE_Y_TOP + 50} width={BLD_W + 8} height={6} fill={C.buildAccentDark} stroke={C.buildOutline} strokeWidth={1} />
    {/* Railing slats */}
    {[...Array(11)].map((_, i) => (
      <rect key={i} x={BLD_X + 8 + i * 50} y={TERRACE_Y_TOP + 10} width={3} height={40} fill={C.buildOutline} opacity={0.5} />
    ))}
    {/* Railing top bar */}
    <rect x={BLD_X - 4} y={TERRACE_Y_TOP + 8} width={BLD_W + 8} height={4} fill={C.buildOutline} />
    {/* SUCCESSLANE sign on top */}
    <rect x={BLD_X + 30} y={TERRACE_Y_TOP - 22} width={BLD_W - 60} height={20} rx={3} fill={C.navy} stroke={C.buildOutline} strokeWidth={1.2} />
    <circle cx={BLD_X + 50} cy={TERRACE_Y_TOP - 12} r={3} fill={C.gold} />
    <text x={BLD_X + BLD_W / 2 + 10} y={TERRACE_Y_TOP - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.white} fontFamily="'Fraunces', serif" style={{ letterSpacing: '0.06em' }}>
      SUCCESSLANE HQ
    </text>
    {/* Flagpole */}
    <rect x={BLD_X + BLD_W - 30} y={TERRACE_Y_TOP - 50} width={2} height={50} fill={C.buildOutline} />
    <motion.path
      d={`M ${BLD_X + BLD_W - 28} ${TERRACE_Y_TOP - 50} L ${BLD_X + BLD_W - 10} ${TERRACE_Y_TOP - 45} L ${BLD_X + BLD_W - 28} ${TERRACE_Y_TOP - 40} Z`}
      fill={C.red}
      stroke={C.buildOutline}
      strokeWidth={0.8}
      animate={{ rotate: [-3, 3, -3] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: `${BLD_X + BLD_W - 28}px`, originY: `${TERRACE_Y_TOP - 45}px` }}
    />
    {/* Celebration label */}
    {celebrating && (
      <motion.g
        initial={{ scale: 0, y: 10 }}
        animate={{ scale: [0, 1.2, 1], y: [10, -4, 0], opacity: [0, 1, 1] }}
        transition={{ duration: 0.7, ease: 'backOut' }}
      >
        <rect x={BLD_X + 100} y={TERRACE_Y_TOP + 14} width={140} height={22} rx={11} fill={C.gold} stroke={C.buildOutline} strokeWidth={1.2} />
        <text x={BLD_X + 170} y={TERRACE_Y_TOP + 29} textAnchor="middle" fontSize={11} fontWeight={800} fill={C.navy}>🎉 PROFILE DELIVERED!</text>
      </motion.g>
    )}
  </g>
);

// ═══ ELEVATOR ═══════════════════════════════════════════════════════════════
const Elevator = ({ y }) => (
  <g>
    <rect x={ELEVATOR_X} y={TERRACE_Y_TOP + 14} width={ELEVATOR_W} height={GROUND_Y - TERRACE_Y_TOP - 14} fill={C.buildSide} stroke={C.buildOutline} strokeWidth={1} />
    {[0,1,2,3,4].map(i => (
      <line key={i} x1={ELEVATOR_X} y1={GROUND_Y - i*FLOOR_H} x2={ELEVATOR_X + ELEVATOR_W} y2={GROUND_Y - i*FLOOR_H} stroke={C.floorLine} strokeWidth={0.8} />
    ))}
    <line x1={ELEVATOR_X + ELEVATOR_W/2} y1={TERRACE_Y_TOP + 20} x2={ELEVATOR_X + ELEVATOR_W/2} y2={y} stroke={C.charShoe} strokeWidth={1} />
    <motion.g
      animate={{ y }}
      initial={{ y: GROUND_Y - 20 }}
      transition={{ type: 'tween', duration: 1.2, ease: 'easeInOut' }}
    >
      <rect x={ELEVATOR_X + 4} y={-FLOOR_H + 6} width={ELEVATOR_W - 8} height={FLOOR_H - 10} rx={3} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={1} />
      <line x1={ELEVATOR_X + ELEVATOR_W/2} y1={-FLOOR_H + 10} x2={ELEVATOR_X + ELEVATOR_W/2} y2={-4} stroke={C.buildOutline} strokeWidth={0.8} />
    </motion.g>
  </g>
);

// ═══ INFO CARD ══════════════════════════════════════════════════════════════
const InfoCard = ({ station, onClose, onContinue, onCta }) => {
  if (!station) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(13, 31, 60, 0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16, zIndex: 10 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 16, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 16, opacity: 0, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: C.white, borderRadius: 16, padding: '24px 26px', maxWidth: 360, width: 'calc(100% - 32px)', boxShadow: '0 18px 50px rgba(13, 31, 60, 0.28)', position: 'relative' }}
        >
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: C.textFaint, fontSize: 18, lineHeight: 1 }}>×</button>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.primary, textTransform: 'uppercase', marginBottom: 4 }}>STEP {station.step}</div>
          <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: "'Fraunces', serif" }}>{station.title}</h3>
          <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.55, color: C.textMid }}>{station.blurb}</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onContinue}
              style={{ flex: 1, padding: '9px 14px', borderRadius: 9, background: C.white, color: C.textMid, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Continue tour</button>
            <button type="button" onClick={() => onCta(station.ctaPrimary)}
              style={{ flex: 1, padding: '9px 14px', borderRadius: 9, background: C.primary, color: C.white, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{station.ctaPrimary.label} →</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══ MAIN COMPONENT ═════════════════════════════════════════════════════════
const BuildingDepartmentJourney = ({ compact = false, onCtaClick }) => {
  const prefersReducedMotion = useReducedMotion();
  const [phase, setPhase] = useState(0);
  const [activeStation, setActiveStation] = useState(null);

  // Phase plan:
  //  0: walk Floor 1 L→R     (3.0s)
  //  1: elevator F1 → F2     (1.3s)
  //  2: walk Floor 2 R→L     (3.0s)
  //  3: elevator F2 → F3     (1.3s)
  //  4: walk Floor 3 L→R     (3.0s)
  //  5: elevator F3 → F4     (1.3s)
  //  6: walk Floor 4 R→L     (3.0s)
  //  7: elevator F4 → F5     (1.3s)
  //  8: walk Floor 5 L→R     (3.0s)
  //  9: elevator F5 → Terrace (1.5s)
  // 10: walk on terrace to centre (1.5s)
  // 11: victory pose + confetti (4.0s)
  // 12: brief reset (0.5s)
  const PHASE_DURATIONS = [3000, 1300, 3000, 1300, 3000, 1300, 3000, 1300, 3000, 1500, 1500, 4000, 500];

  useEffect(() => {
    if (prefersReducedMotion || activeStation) return;
    const id = setTimeout(() => setPhase((p) => (p + 1) % PHASE_DURATIONS.length), PHASE_DURATIONS[phase]);
    return () => clearTimeout(id);
  }, [phase, prefersReducedMotion, activeStation]);

  const handleStationClick = (idx) => setActiveStation(STATIONS[idx]);
  const handleClose = () => setActiveStation(null);
  const handleContinue = () => setActiveStation(null);
  const handleCta = (cta) => {
    if (onCtaClick) onCtaClick(cta);
    else window.location.href = cta.to;
  };

  const xStart = BLD_X + 50;
  const xEnd = BLD_X + BLD_W - 105;
  const xMid = BLD_X + BLD_W / 2;

  // For each phase: { x, y, mode, flipX }
  const charState = (() => {
    switch (phase) {
      case 0:  return { x: xEnd,   y: floorY(0), mode: 'walking', flipX: false };
      case 1:  return { x: xEnd,   y: floorY(1), mode: 'idle',    flipX: false, elev: true };
      case 2:  return { x: xStart, y: floorY(1), mode: 'walking', flipX: true };
      case 3:  return { x: xStart, y: floorY(2), mode: 'idle',    flipX: true, elev: true };
      case 4:  return { x: xEnd,   y: floorY(2), mode: 'walking', flipX: false };
      case 5:  return { x: xEnd,   y: floorY(3), mode: 'idle',    flipX: false, elev: true };
      case 6:  return { x: xStart, y: floorY(3), mode: 'walking', flipX: true };
      case 7:  return { x: xStart, y: floorY(4), mode: 'idle',    flipX: true, elev: true };
      case 8:  return { x: xEnd,   y: floorY(4), mode: 'walking', flipX: false };
      case 9:  return { x: xEnd,   y: TERRACE_FLOOR_Y, mode: 'idle', flipX: false, elev: true };
      case 10: return { x: xMid,   y: TERRACE_FLOOR_Y, mode: 'walking', flipX: true };
      case 11: return { x: xMid,   y: TERRACE_FLOOR_Y, mode: 'victory', flipX: false };
      case 12: return { x: BLD_X + 50, y: floorY(0), mode: 'idle', flipX: false };
      default: return { x: xStart, y: floorY(0), mode: 'walking', flipX: false };
    }
  })();
  const elevatorPhase = !!charState.elev;
  const elevatorY = phase === 1 ? floorY(1) - 20 :
                    phase === 3 ? floorY(2) - 20 :
                    phase === 5 ? floorY(3) - 20 :
                    phase === 7 ? floorY(4) - 20 :
                    phase === 9 ? TERRACE_FLOOR_Y - 20 :
                    charState.y - 20;
  const inElevator = elevatorPhase;
  const charX = inElevator ? ELEVATOR_X + ELEVATOR_W / 2 : charState.x;
  const charY = inElevator ? charState.y - 4 : charState.y - 4;
  // For pill: which floor is character on
  const phaseFloorMap = [0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 5, 0];
  const activeFloor = phaseFloorMap[phase] ?? 0;
  const onTerrace = phase >= 9 && phase <= 11;
  const celebrating = phase === 11;

  const height = compact ? 420 : 600;

  return (
    <div
      style={{
        position: 'relative', width: '100%',
        borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden',
        background: C.skyTop,
        boxShadow: '0 6px 24px rgba(13, 31, 60, 0.06)',
      }}
      data-testid="building-journey"
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height }}
        role="img"
        aria-label="Successlane HQ — a 5-floor building cutaway with the character climbing through each stage of the workflow, ending in victory on the rooftop terrace. Trees, shops, and pedestrians surround the building."
      >
        <defs>
          <linearGradient id="skyBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={C.skyTop} />
            <stop offset="100%" stopColor={C.skyBot} />
          </linearGradient>
          <linearGradient id="groundBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={C.groundTop} />
            <stop offset="100%" stopColor={C.groundBot} />
          </linearGradient>
        </defs>

        {/* Sky */}
        <rect x={0} y={0} width={VB_W} height={GROUND_Y} fill="url(#skyBg)" />
        {/* Sun */}
        <circle cx={1280} cy={80} r={24} fill="#FBBF24" opacity={0.85} />
        <circle cx={1280} cy={80} r={34} fill="#FBBF24" opacity={0.2} />
        {/* Distant clouds */}
        <ellipse cx={180} cy={60} rx={36} ry={10} fill={C.white} opacity={0.7} />
        <ellipse cx={210} cy={55} rx={28} ry={9} fill={C.white} opacity={0.8} />
        <ellipse cx={1080} cy={100} rx={32} ry={10} fill={C.white} opacity={0.7} />
        <ellipse cx={1110} cy={95} rx={26} ry={8} fill={C.white} opacity={0.85} />

        {/* Ground (grass tint) */}
        <rect x={0} y={GROUND_Y} width={VB_W} height={VB_H - GROUND_Y} fill="url(#groundBg)" />
        {/* Sidewalk in front of building */}
        <rect x={0} y={GROUND_Y} width={VB_W} height={ROAD_Y - GROUND_Y - 50} fill={C.sidewalk} />
        <line x1={0} y1={GROUND_Y} x2={VB_W} y2={GROUND_Y} stroke={C.buildOutline} strokeWidth={1.2} />
        {/* Road */}
        <rect x={0} y={ROAD_Y - 50} width={VB_W} height={70} fill={C.road} stroke={C.roadEdge} strokeWidth={1.5} />
        {/* Centre dashes */}
        {[...Array(20)].map((_, i) => (
          <rect key={i} x={i * 80 + 10} y={ROAD_Y - 18} width={36} height={3} rx={1} fill={C.white} opacity={0.8} />
        ))}
        {/* Curb shadow */}
        <line x1={0} y1={ROAD_Y - 50} x2={VB_W} y2={ROAD_Y - 50} stroke={C.roadEdge} strokeWidth={1.2} />
        <line x1={0} y1={ROAD_Y + 20} x2={VB_W} y2={ROAD_Y + 20} stroke={C.roadEdge} strokeWidth={1.2} />

        {/* ── BUILDING SHELL ───────────────────────────────────────── */}
        <rect x={BLD_X} y={BLD_Y_TOP} width={BLD_W} height={FLOORS * FLOOR_H} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.4} />
        {[1,2,3,4].map(i => (
          <line key={i} x1={BLD_X} y1={GROUND_Y - i * FLOOR_H} x2={BLD_X + BLD_W} y2={GROUND_Y - i * FLOOR_H} stroke={C.buildOutline} strokeWidth={1} />
        ))}
        {/* Terrace structure on top */}
        <Terrace celebrating={celebrating} />

        {/* Each floor clickable hit area */}
        {[0,1,2,3,4].map(idx => (
          <rect
            key={idx}
            x={BLD_X} y={GROUND_Y - (idx + 1) * FLOOR_H}
            width={BLD_W - ELEVATOR_W - 5} height={FLOOR_H}
            fill={activeFloor === idx + 1 ? C.primaryLight : 'transparent'}
            opacity={activeFloor === idx + 1 ? 0.3 : 0}
            style={{ cursor: 'pointer' }}
            onClick={() => handleStationClick(idx)}
          />
        ))}

        {/* Floor contents */}
        <Floor1 />
        <Floor2 />
        <Floor3 />
        <Floor4 />
        <Floor5 />

        {/* Elevator (above floors so cab is visible) */}
        <Elevator y={elevatorY} />

        {/* ── CHARACTER ─────────────────────────────────────────────── */}
        {!prefersReducedMotion && (
          <motion.g
            animate={{ x: charX, y: charY }}
            initial={{ x: BLD_X + 50, y: floorY(0) - 4 }}
            transition={{ duration: charState.mode === 'walking' ? 3 : (elevatorPhase ? 1.2 : 0.7), ease: elevatorPhase ? 'easeInOut' : 'linear' }}
          >
            <Character mode={charState.mode} flipX={charState.flipX} />
          </motion.g>
        )}
        {prefersReducedMotion && (
          <g transform={`translate(${BLD_X + 60}, ${floorY(0) - 4})`}>
            <Character mode="idle" flipX={false} />
          </g>
        )}

        {/* ── CONFETTI BURST ON TERRACE ───────────────────────────── */}
        {celebrating && (
          <g>
            {[...Array(28)].map((_, i) => {
              const angle = (i / 28) * Math.PI * 2;
              const r = 70 + (i % 4) * 25;
              const dx = Math.cos(angle) * r;
              const dy = -Math.abs(Math.sin(angle)) * r * 0.9;
              const colors = [C.red, C.gold, C.green, C.primary, C.buildAccent, C.ped1, C.ped5];
              return (
                <motion.rect
                  key={i}
                  x={xMid} y={TERRACE_FLOOR_Y - 30}
                  width={5} height={9} rx={1.5}
                  fill={colors[i % colors.length]}
                  initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
                  animate={{
                    x: [0, dx],
                    y: [0, dy, dy + 70],
                    opacity: [0, 1, 0],
                    rotate: [0, 200 * (i % 2 === 0 ? 1 : -1), 540],
                  }}
                  transition={{ duration: 3, ease: 'easeOut', repeat: Infinity, repeatDelay: 1.2, delay: (i % 6) * 0.04 }}
                />
              );
            })}
          </g>
        )}

        {/* ── CITY: LEFT SIDE (around x=0 to BLD_X) ────────────────────── */}
        <Shop x={140} y={GROUND_Y} name="CAFÉ" accent={C.red} />
        <Shop x={310} y={GROUND_Y} name="BOOKS" accent={C.primary} />
        <PalmTree x={50}  y={GROUND_Y - 4} scale={1.0} />
        <PalmTree x={240} y={GROUND_Y - 4} scale={0.9} />
        <PalmTree x={380} y={GROUND_Y - 4} scale={1.0} />
        <RoundTree x={400} y={GROUND_Y - 4} scale={0.85} />
        {/* Pedestrians on left */}
        <Pedestrian x={75}  y={GROUND_Y} color={C.ped1} scale={1.1} />
        <Pedestrian x={185} y={GROUND_Y} color={C.ped2} scale={1.05} />
        <Pedestrian x={215} y={GROUND_Y} color={C.ped3} scale={1.0} />
        <Pedestrian x={350} y={GROUND_Y} color={C.ped4} scale={1.0} />
        {/* Street lamp */}
        <StreetLamp x={120} y={GROUND_Y} />
        <StreetLamp x={290} y={GROUND_Y} />

        {/* ── CITY: RIGHT SIDE (BLD_X + BLD_W to VB_W) ─────────────────── */}
        <Shop x={1100} y={GROUND_Y} name="MART" accent={C.gold} />
        <Shop x={1270} y={GROUND_Y} name="TECH" accent={C.green} />
        <PalmTree x={1020} y={GROUND_Y - 4} scale={0.95} />
        <PalmTree x={1200} y={GROUND_Y - 4} scale={0.9} />
        <RoundTree x={1360} y={GROUND_Y - 4} scale={0.9} />
        {/* Pedestrians on right */}
        <Pedestrian x={1050} y={GROUND_Y} color={C.ped5} scale={1.05} />
        <Pedestrian x={1175} y={GROUND_Y} color={C.ped1} scale={1.0} />
        <Pedestrian x={1340} y={GROUND_Y} color={C.ped2} scale={1.1} />
        <StreetLamp x={1060} y={GROUND_Y} />
        <StreetLamp x={1240} y={GROUND_Y} />

        {/* ── In front of building (near entrance — public standing) ── */}
        <Pedestrian x={BLD_X + 80}  y={GROUND_Y + 32} color={C.ped3} scale={1.0} />
        <Pedestrian x={BLD_X + 470} y={GROUND_Y + 30} color={C.ped4} scale={1.0} />
        <Pedestrian x={BLD_X + 200} y={GROUND_Y + 45} color={C.ped2} scale={0.95} />

        {/* ── CHARGING STATION + MOTORBIKES (near road, right side) ── */}
        <ChargingStation x={1010} y={ROAD_Y - 56} />
        <Motorbike x={985}  y={ROAD_Y - 52} color={C.red} />
        <Motorbike x={1040} y={ROAD_Y - 52} color={C.primary} />

        {/* A second cluster on the left of building near road */}
        <Motorbike x={300} y={ROAD_Y - 52} color={C.ped5} />
        <Motorbike x={350} y={ROAD_Y - 52} color={C.gold} />

        {/* ── Moving car ───────────────────────────────────────────────── */}
        {!prefersReducedMotion && (
          <motion.g
            initial={{ x: -100 }}
            animate={{ x: VB_W + 100 }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          >
            <Car x={0} y={ROAD_Y - 18} color={C.primary} />
          </motion.g>
        )}

        {/* Distant scooter (slower) */}
        {!prefersReducedMotion && (
          <motion.g
            initial={{ x: VB_W + 100 }}
            animate={{ x: -100 }}
            transition={{ duration: 14, repeat: Infinity, ease: 'linear', delay: 5 }}
          >
            <Motorbike x={0} y={ROAD_Y + 5} color={C.gold} />
          </motion.g>
        )}

      </svg>

      {/* Step pill */}
      <div style={{
        position: 'absolute', top: 14, right: 14,
        background: C.white, border: `1px solid ${C.border}`,
        borderRadius: 99, padding: '5px 13px',
        fontSize: 11, fontWeight: 600, color: C.textMid,
        display: 'flex', alignItems: 'center', gap: 7,
        boxShadow: '0 2px 8px rgba(13, 31, 60, 0.08)',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: onTerrace ? C.gold : C.primary }} />
        {onTerrace
          ? '🎉 Delivered! Profile certified & shipped'
          : `Floor ${activeFloor} of 5 · ${STATIONS[Math.max(0, activeFloor - 1)].title}`}
      </div>

      {!compact && (
        <div style={{
          position: 'absolute', bottom: 14, left: 18,
          fontSize: 12, color: C.textMid,
          background: 'rgba(255, 255, 255, 0.85)',
          padding: '4px 10px', borderRadius: 99,
          backdropFilter: 'blur(4px)',
        }}>
          Click any floor to learn how it works
        </div>
      )}

      <InfoCard
        station={activeStation}
        onClose={handleClose}
        onContinue={handleContinue}
        onCta={handleCta}
      />
    </div>
  );
};

export default BuildingDepartmentJourney;
